import { Injectable } from '@angular/core';
import {
  CustomerInfo,
  LOG_LEVEL,
  Purchases,
  PurchasesOffering,
  PurchasesOfferings,
} from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { Package, PurchaseResult } from '@revenuecat/purchases-js';

import { environment } from 'src/environments/environment';
import { logger } from '#lib/logger';
import { IPurchaseService } from './purchase.interface';
import { BehaviorSubject } from 'rxjs';
import { ENTITLEMENT_NAME, Product } from '#models/subscription/product.model';
import { RedemptionService } from '#services/redemption/redemption.service';
import { B2B_OFFERING, IOS_B2B_OFFERING } from '#models/subscription/user-redemption.model';

@Injectable({
  providedIn: 'root',
})
export class PurchaseService implements IPurchaseService {
  products$ = new BehaviorSubject<Product[]>([]);
  userHasSubscribedBefore$ = new BehaviorSubject<boolean>(false);

  constructor(private redemptionService: RedemptionService) {}

  async init(): Promise<void> {
    const platform = Capacitor.getPlatform();

    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    await Purchases.configure({
      apiKey: environment.revenueCatApiKey[platform as keyof typeof environment.revenueCatApiKey],
    });

    logger.log(platform, 'PurchaseService.init -> Capacitor RevenueCat configured');
  }

  isSandbox(): boolean {
    throw new Error('isSandbox is not supported on native platforms.');
  }

  async logInCustomerInfo(userId: string): Promise<void> {
    try {
      await Purchases.logIn({ appUserID: userId });
      logger.log(userId, 'PurchaseService.logInCustomerInfo -> User logged in successfully');
    } catch (error: unknown) {
      // Silent failure - allows login flow to continue even if RevenueCat is unavailable.
      // Subscription features will handle their own errors when calling getCustomerInfo().
      logger.log(error, 'PurchaseService.logInCustomerInfo -> Error (silent failure)');
    }
  }

  async logOutCustomerInfo(): Promise<void> {
    logger.log('', 'PurchaseService.logOutCustomerInfo -> Logging out user');

    this.userHasSubscribedBefore$.next(false);

    await Purchases.logOut();
  }

  async isConfigured(): Promise<boolean> {
    const { isConfigured } = await Purchases.isConfigured();

    logger.log(isConfigured, 'PurchaseService.isConfigured -> Is configured');
    return isConfigured;
  }

  async getCustomerInfo(): Promise<CustomerInfo> {
    const { customerInfo } = await Purchases.getCustomerInfo();

    logger.log(customerInfo, 'PurchaseService.getCustomerInfo -> Customer Info retrieved');
    return customerInfo;
  }

  async getOfferings(): Promise<PurchasesOffering> {
    const offerings: PurchasesOfferings = await Purchases.getOfferings();

    // Check if the user has redeemed an offer code, if so, we show them the B2B offering.
    const offerCodeRedeemed = await this.redemptionService.verifyUserRedemption(true);
    // Determine the correct offering key based on platform. This is important because iOS requires the paywall to display
    // Terms of Use and Privacy Policy links.
    const offeringKey = Capacitor.getPlatform() === 'ios' ? IOS_B2B_OFFERING : B2B_OFFERING;

    const offering = !offerCodeRedeemed ? offerings.current! : offerings.all[offeringKey]!;

    logger.log(offering, 'PurchaseService.getOfferings -> Offering packages retrieved');

    return offering;
  }

  async checkIfUserIsSubscribed(entitlements?: 'active' | 'all'): Promise<boolean> {
    const customerInfo = await this.getCustomerInfo();
    const hasActiveSubscription =
      ENTITLEMENT_NAME in customerInfo.entitlements[entitlements || 'active'];

    if (entitlements && entitlements === 'all') {
      this.userHasSubscribedBefore$.next(hasActiveSubscription);
    }

    return hasActiveSubscription;
  }

  async purchaseProduct(
    _rcPackage: Package,
    _nextFn?: (purchaseResult: PurchaseResult | null) => void
  ): Promise<void> {
    throw new Error('purchaseProduct is not supported on native platforms.');
  }

  resetPurchases() {
    this.products$.next([]);
    this.userHasSubscribedBefore$.next(false);
    logger.log('complete', 'PurchaseService.resetPurchases');
  }
}
